import type { AutomobileBrand, AutomobileCatalog, AutomobileModel } from '../types/automobile';
import { dedupeAutomobileModels, dedupeAutomobileModelsStrict, getAutomobileModelStableKey } from './automobileModelDisplay';
import { cacheBustAutomobileImageUrl, isUserAutomobileImageUrl } from './automobileImageUrl';
import { modelImageFrameFromRow, logoFrameFromRow } from './automobileModelImageFrame';

export type SupabaseBrandRow = {
  id: string;
  slug: string;
  name: string;
  country_code: string | null;
  logo_url: string | null;
  updated_at?: string | null;
  logo_focal_x?: number | null;
  logo_focal_y?: number | null;
  logo_zoom?: number | null;
  description: string | null;
  founded: number | null;
  headquarters: string | null;
  website: string | null;
  highlights: string[] | null;
  legal_name: string | null;
  company_status: string | null;
  parent_company: string | null;
  successor_brand: string | null;
  closed_year: number | null;
  closure_reason: string | null;
  verification_level: string | null;
  sources: { title: string; url: string }[] | null;
  is_hidden?: boolean | null;
};

export type SupabaseModelRow = {
  id: string;
  brand_id: string;
  model_key?: string | null;
  name: string;
  display_name?: string | null;
  generation_code?: string | null;
  variant_label?: string | null;
  year_from?: number | null;
  year_from_month?: number | null;
  year_to?: number | null;
  body_type: string;
  image_url: string | null;
  updated_at?: string | null;
  image_generic?: boolean | null;
  image_focal_x?: number | null;
  image_focal_y?: number | null;
  image_zoom?: number | null;
  production_years?: number[] | null;
  is_hidden?: boolean | null;
};

function legacyModelKey(brandSlug: string, name: string, bodyType: string): string {
  return [brandSlug, name, '', '', '', '', '', bodyType].join('|');
}

/** Sitede yayımlanacak marka/model satırları (gizliler hariç) */
export function filterPublishedAutomobileRows(
  brandRows: SupabaseBrandRow[],
  modelRows: SupabaseModelRow[],
): { brandRows: SupabaseBrandRow[]; modelRows: SupabaseModelRow[] } {
  const visibleBrands = brandRows.filter((b) => !b.is_hidden);
  const hiddenBrandIds = new Set(brandRows.filter((b) => b.is_hidden).map((b) => b.id));
  const visibleModels = modelRows.filter((m) => !m.is_hidden && !hiddenBrandIds.has(m.brand_id));
  return { brandRows: visibleBrands, modelRows: visibleModels };
}

function rowStableKey(brandSlug: string, row: SupabaseModelRow): string {
  return (
    row.model_key?.trim() ||
    getAutomobileModelStableKey(brandSlug, {
      name: row.name,
      displayName: row.display_name || undefined,
      generationCode: row.generation_code || undefined,
      variantLabel: row.variant_label || undefined,
      yearFrom: row.year_from ?? undefined,
      yearFromMonth: row.year_from_month ?? undefined,
      yearTo: row.year_to ?? undefined,
      bodyType: row.body_type as AutomobileModel['bodyType'],
      imageUrl: '',
    })
  );
}

function supabaseRowToAutomobileModel(row: SupabaseModelRow): AutomobileModel {
  const frame = modelImageFrameFromRow(row);
  return {
    name: row.name,
    displayName: row.display_name || undefined,
    generationCode: row.generation_code || undefined,
    variantLabel: row.variant_label || undefined,
    yearFrom: row.year_from ?? undefined,
    yearFromMonth: row.year_from_month ?? undefined,
    yearTo: row.year_to ?? undefined,
    productionYears: row.production_years?.length ? row.production_years : undefined,
    bodyType: row.body_type as AutomobileModel['bodyType'],
    imageUrl: cacheBustAutomobileImageUrl(row.image_url, row.updated_at) || '',
    imageGeneric: Boolean(row.image_generic),
    imageFocalX: frame.focalX,
    imageFocalY: frame.focalY,
    imageZoom: frame.zoom,
  };
}

function shouldPreferSupabaseImage(base: AutomobileModel, row: SupabaseModelRow): boolean {
  const url = row.image_url?.trim();
  if (!url) return false;
  if (isUserAutomobileImageUrl(url)) return true;
  if (row.image_generic === false) return true;
  if (base.imageGeneric) return true;
  return url !== (base.imageUrl || '').trim();
}

function mergeBaseModelWithSupabase(base: AutomobileModel, row: SupabaseModelRow): AutomobileModel {
  const patch = supabaseRowToAutomobileModel(row);
  const imageUrl = shouldPreferSupabaseImage(base, row) ? patch.imageUrl : base.imageUrl || patch.imageUrl;
  const productionYears = patch.productionYears?.length
    ? patch.productionYears
    : base.productionYears;

  return {
    ...base,
    displayName: patch.displayName ?? base.displayName,
    imageUrl,
    imageGeneric: shouldPreferSupabaseImage(base, row) ? patch.imageGeneric : base.imageGeneric,
    imageFocalX: patch.imageFocalX ?? base.imageFocalX,
    imageFocalY: patch.imageFocalY ?? base.imageFocalY,
    imageZoom: patch.imageZoom ?? base.imageZoom,
    productionYears,
  };
}

function mergeBrandMeta(base: AutomobileBrand, row: SupabaseBrandRow | undefined): AutomobileBrand {
  if (!row) return base;
  const hasLogoFrame =
    row.logo_focal_x != null || row.logo_focal_y != null || row.logo_zoom != null;
  const logoFrame = hasLogoFrame ? logoFrameFromRow(row) : null;
  return {
    ...base,
    name: row.name || base.name,
    logoUrl: cacheBustAutomobileImageUrl(row.logo_url, row.updated_at) || base.logoUrl,
    logoFocalX: logoFrame?.focalX ?? base.logoFocalX,
    logoFocalY: logoFrame?.focalY ?? base.logoFocalY,
    logoZoom: logoFrame?.zoom ?? base.logoZoom,
    country: row.country_code || base.country,
    description: row.description || base.description,
    founded: row.founded ?? base.founded,
    headquarters: row.headquarters || base.headquarters,
    website: row.website || base.website,
    highlights: row.highlights || base.highlights,
    legalName: row.legal_name || base.legalName,
    companyStatus: (row.company_status as AutomobileBrand['companyStatus']) || base.companyStatus,
    parentCompany: row.parent_company ?? base.parentCompany,
    successorBrand: row.successor_brand ?? base.successorBrand,
    closedYear: row.closed_year ?? base.closedYear,
    closureReason: row.closure_reason ?? base.closureReason,
    verificationLevel:
      (row.verification_level as AutomobileBrand['verificationLevel']) || base.verificationLevel,
    sources: row.sources || base.sources,
  };
}

function supabaseBrandToAutomobile(
  row: SupabaseBrandRow,
  models: AutomobileModel[],
  fallback?: AutomobileBrand,
): AutomobileBrand {
  const logoFrame = logoFrameFromRow(row);
  const fromDb: AutomobileBrand = {
    slug: row.slug,
    name: row.name,
    logoUrl: cacheBustAutomobileImageUrl(row.logo_url, row.updated_at) || '',
    logoFocalX: logoFrame.focalX,
    logoFocalY: logoFrame.focalY,
    logoZoom: logoFrame.zoom,
    country: row.country_code || undefined,
    description: row.description || '',
    founded: row.founded ?? undefined,
    headquarters: row.headquarters || undefined,
    website: row.website || undefined,
    highlights: row.highlights || undefined,
    legalName: row.legal_name || undefined,
    companyStatus: (row.company_status as AutomobileBrand['companyStatus']) || undefined,
    parentCompany: row.parent_company ?? undefined,
    successorBrand: row.successor_brand ?? undefined,
    closedYear: row.closed_year ?? undefined,
    closureReason: row.closure_reason ?? undefined,
    verificationLevel: (row.verification_level as AutomobileBrand['verificationLevel']) || undefined,
    sources: row.sources || undefined,
    models,
  };
  return fallback ? mergeBrandMeta(fallback, row) : fromDb;
}

/**
 * Forum sistemi gibi: canlı veri yalnızca Supabase'ten.
 * catalog.json yalnızca boş alanları doldurmak için yedek meta kullanır.
 */
export function buildCatalogFromSupabase(
  fallback: AutomobileCatalog,
  brandRows: SupabaseBrandRow[],
  modelRows: SupabaseModelRow[],
): AutomobileCatalog {
  if (!brandRows.length) {
    return {
      generatedAt: new Date().toISOString(),
      brandCount: 0,
      brandsWithModels: 0,
      modelCount: 0,
      verifiedBrandCount: 0,
      defunctBrandCount: 0,
      brands: [],
    };
  }

  const published = filterPublishedAutomobileRows(brandRows, modelRows);
  brandRows = published.brandRows;
  modelRows = published.modelRows;

  if (!brandRows.length) {
    return {
      generatedAt: new Date().toISOString(),
      brandCount: 0,
      brandsWithModels: 0,
      modelCount: 0,
      verifiedBrandCount: 0,
      defunctBrandCount: 0,
      brands: [],
    };
  }

  const fallbackBrandBySlug = new Map(fallback.brands.map((b) => [b.slug, b]));
  const brandIdToSlug = new Map(brandRows.map((b) => [b.id, b.slug]));

  const modelsBySlug = new Map<string, AutomobileModel[]>();
  for (const row of modelRows) {
    const slug = brandIdToSlug.get(row.brand_id);
    if (!slug) continue;
    const list = modelsBySlug.get(slug) || [];
    list.push(supabaseRowToAutomobileModel(row));
    modelsBySlug.set(slug, list);
  }

  for (const [slug, list] of modelsBySlug) {
    modelsBySlug.set(slug, dedupeAutomobileModelsStrict(slug, dedupeAutomobileModels(slug, list)));
  }

  const brandsOut: AutomobileBrand[] = brandRows.map((sb) => {
    const models = modelsBySlug.get(sb.slug) || [];
    const fb = fallbackBrandBySlug.get(sb.slug);
    return supabaseBrandToAutomobile(sb, models, fb);
  });

  brandsOut.sort((a, b) => a.name.localeCompare(b.name, 'tr', { sensitivity: 'base' }));

  const modelCount = brandsOut.reduce((s, b) => s + b.models.length, 0);
  const brandsWithModels = brandsOut.filter((b) => b.models.length > 0).length;
  const verifiedBrandCount = brandsOut.filter((b) => b.verificationLevel === 'verified').length;
  const defunctBrandCount = brandsOut.filter((b) => b.companyStatus === 'defunct').length;

  return {
    generatedAt: new Date().toISOString(),
    brandCount: brandsOut.length,
    brandsWithModels,
    modelCount,
    verifiedBrandCount,
    defunctBrandCount,
    brands: brandsOut,
  };
}

/** Site ve admin’de aynı yayınlanan model sayısı */
export function countPublishedAutomobileModels(
  brandRows: SupabaseBrandRow[],
  modelRows: SupabaseModelRow[],
  fallback?: AutomobileCatalog,
): number {
  return buildCatalogFromSupabase(
    fallback ?? {
      generatedAt: '',
      brandCount: 0,
      brandsWithModels: 0,
      modelCount: 0,
      brands: [],
    },
    brandRows,
    modelRows,
  ).modelCount;
}

/** @deprecated buildCatalogFromSupabase kullanın */
export function mergeCatalogWithSupabase(
  base: AutomobileCatalog,
  brandRows: SupabaseBrandRow[],
  modelRows: SupabaseModelRow[],
): AutomobileCatalog {
  const brandIdToSlug = new Map(brandRows.map((b) => [b.id, b.slug]));
  const brandBySlug = new Map(brandRows.map((b) => [b.slug, b]));

  const supabaseBySlugKey = new Map<string, SupabaseModelRow>();
  for (const row of modelRows) {
    const slug = brandIdToSlug.get(row.brand_id);
    if (!slug) continue;
    const stable = rowStableKey(slug, row);
    supabaseBySlugKey.set(`${slug}|${stable}`, row);
    const legacy = legacyModelKey(slug, row.name, row.body_type);
    if (!supabaseBySlugKey.has(`${slug}|${legacy}`)) {
      supabaseBySlugKey.set(`${slug}|${legacy}`, row);
    }
    const nameBody = `${slug}|${row.name.toLowerCase()}|${row.body_type}`;
    if (!supabaseBySlugKey.has(nameBody)) {
      supabaseBySlugKey.set(nameBody, row);
    }
  }

  const usedSupabaseIds = new Set<string>();

  const brandsOut: AutomobileBrand[] = base.brands.map((baseBrand) => {
    const sb = brandBySlug.get(baseBrand.slug);
    const mergedModels = baseBrand.models.map((m) => {
      const stable = getAutomobileModelStableKey(baseBrand.slug, m);
      const row =
        supabaseBySlugKey.get(`${baseBrand.slug}|${stable}`) ||
        supabaseBySlugKey.get(`${baseBrand.slug}|${legacyModelKey(baseBrand.slug, m.name, m.bodyType)}`) ||
        supabaseBySlugKey.get(`${baseBrand.slug}|${m.name.toLowerCase()}|${m.bodyType}`);
      if (!row) return m;
      usedSupabaseIds.add(row.id);
      return mergeBaseModelWithSupabase(m, row);
    });

    if (sb) {
      for (const row of modelRows) {
        if (brandIdToSlug.get(row.brand_id) !== baseBrand.slug) continue;
        if (usedSupabaseIds.has(row.id)) continue;
        const asModel = supabaseRowToAutomobileModel(row);
        const nb = `${asModel.name.toLowerCase()}|${asModel.bodyType}`;
        const exists = mergedModels.some(
          (m) => `${m.name.toLowerCase()}|${m.bodyType}` === nb,
        );
        if (!exists) {
          mergedModels.push(asModel);
          usedSupabaseIds.add(row.id);
        }
      }
    }

    const modelsDeduped = dedupeAutomobileModels(baseBrand.slug, mergedModels);
    return mergeBrandMeta({ ...baseBrand, models: modelsDeduped }, sb);
  });

  const knownSlugs = new Set(base.brands.map((b) => b.slug));
  for (const sb of brandRows) {
    if (knownSlugs.has(sb.slug)) continue;
    const models = dedupeAutomobileModels(
      sb.slug,
      modelRows
        .filter((m) => brandIdToSlug.get(m.brand_id) === sb.slug)
        .map(supabaseRowToAutomobileModel),
    );
    if (!models.length) continue;
    brandsOut.push(
      mergeBrandMeta(
        {
          slug: sb.slug,
          name: sb.name,
          logoUrl: cacheBustAutomobileImageUrl(sb.logo_url, sb.updated_at) || '',
          models,
        },
        sb,
      ),
    );
  }

  brandsOut.sort((a, b) => a.name.localeCompare(b.name, 'tr', { sensitivity: 'base' }));

  const modelCount = brandsOut.reduce((s, b) => s + b.models.length, 0);
  const brandsWithModels = brandsOut.filter((b) => b.models.length > 0).length;

  return {
    ...base,
    brandCount: brandsOut.length,
    brandsWithModels,
    modelCount,
    brands: brandsOut,
  };
}
